import React, { useState, useMemo, useEffect } from 'react';
import { map, defaultTo, isEqual, isEmpty, noop } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { button as Button } from 'modules/button';
import { getTabView } from 'resolvers/widgetsTabFlow';
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';
import { NAVIGATION_TABS_TYPE } from 'constants/common';

// Plain theme tabs
const PlainNavigation = ( { tabs, activeTabIndex, onClickHandler, onTabActionClick, navType } ) => {
	const navigationPlainClassName = classnames( 'ui-widgets-tab-flow__navigation-plain', {
		'ui-widgets-tab-flow__navigation-plain--secondary': NAVIGATION_TABS_TYPE.SECONDARY === navType
	} );
	return (
		<div className={ navigationPlainClassName }>
			{ map( tabs, ( tab, index ) => {

				// dynamic classes for the tab
				const tabClass = classnames( 'ui-widgets-tab-flow__navigation-plain__tab', {
					'ui-widgets-tab-flow__navigation-plain__tab--selected': index === activeTabIndex,
				} );

				const tabTextClass = classnames( 'ui-widgets-tab-flow__navigation-plain__tab__text', {
					'ui-widgets-tab-flow__navigation-plain__tab__text--secondary': NAVIGATION_TABS_TYPE.SECONDARY === navType
				} );

				// render based on tab action
				if( ! isEmpty( tab.action ) ) {
					return (
						<div key={ index } className={ tabClass }>
							<div className='ui-widgets-tab-flow__navigation-plain__tab__text' onClick={ () => onClickHandler( index ) }>
								{ getLabel( tab.title ) }
							</div>
							<div className='ui-widgets-tab-flow__navigation-plain__tab__action' onClick={ () => onTabActionClick( { tab, action: tab.action } ) }>
								<Button
									className="ui-widgets-tab-flow__navigation-plain__tab__action__button"
									title={ tab.action.title }
									size='extra-small'
									outline={ true }
									hover={ true }
								/>
							</div>
						</div>
					);
				}

				// return normal tab view
				return (
					<div key={ index } className={ tabClass } onClick={ () => onClickHandler( index ) }>
						<div className={ tabTextClass }>
							{ getLabel( tab.title ) }
						</div>
					</div>
				);
			} ) }
		</div>
	);
 };

// set display name
PlainNavigation.displayName = 'widgetsTabFlow.PlainNavigation';

/*-------*/

//  default theme tabs
const DefaultNavigation = ( { tabs, activeTabIndex, onClickHandler } ) => {
	return (
		<div className="ui-widgets-tab-flow__navigation-default">
			{ map( tabs, ( tab, index ) => {
				return (
					<div
						key={ index }
						className="ui-widgets-tab-flow__navigation-default__item"
					>
						<Button
							className="ui-widgets-tab-flow__navigation-default__item__button"
							size="large"
							title={ tab.title }
							onClick={ ( ) => onClickHandler( index ) }
							outline={ index !== activeTabIndex }
							disabled={ tab.disabled }
						/>
					</div>
				);
			} ) }
		</div>
	);
 };

// set display name
DefaultNavigation.displayName = 'widgetsTabFlow.DefaultNavigation';

/* -------- */

/**
 * @type { object }
 * @desc A React presentation/dumb view component.
 * @example
 * import { widgetsTabFlow as WidgetsTabFlowView } from 'modules/widgetsTabFlow';
 *
 * // render
 * <WidgetsTabFlowView propName='propValue'/>
 */

export const widgetsTabFlow = ( props ) => {

	// extract props
	const {
		activeTabIndex: _activeTabIndex,
		tabs: _tabs,
		emptyTabsMessage,
		...passDownProps
	} = props;

	// selected tab index
	const [ activeTabIndex, setActiveTabIndex ] = useState( _activeTabIndex );
	useEffect( ( ) => setActiveTabIndex( _activeTabIndex ), [ _tabs, _activeTabIndex ] );

	// for changing the state and passing to the props
	const handleChange = ( index ) => {
		setActiveTabIndex( index );
		props.onTabChange( index );
	};

	// store tabs as static data
	const tabs = useMemo( () => {
		return map( _tabs, ( step ) => ( {
			view: getTabView( step.name ), // resolve and store widget for the tab
			...step,
		} ) );
	}, [ _tabs ] );

	// get active tab ( fallback to first tab )
	const activeTab = defaultTo( tabs[ activeTabIndex ], { } );

	// dynamic class name
	const widgetClassName = classnames( 'ui-widgets-tab-flow', props.className );

	return (
		<div className={ widgetClassName }>
			{ /* navigation */ }
			{ isEqual( props.theme, 'plain' ) ? (
				<PlainNavigation
					tabs={ tabs }
					activeTabIndex={ activeTabIndex }
					onClickHandler={ handleChange }
					onTabActionClick={ props.onTabActionClick }
					navType={ props.navType }
				/>
			) : (
				<DefaultNavigation
					tabs={ tabs }
					activeTabIndex={ activeTabIndex }
					onClickHandler={ handleChange }
				/>
			) }

			{ /* tab's widget container */ }
			{ !isEmpty( activeTab ) ? (
				<div className="ui-widgets-tab-flow__tab-container">
					<activeTab.view
						key={ activeTabIndex }
						tabIndex={ activeTabIndex }
						widget={ activeTab }
						{ ...passDownProps }
					/>
				</div>
			) : (
				<div className="ui-widgets-tab-flow__empty-tab-container">
					<Icon name="tabs" width="150" height="150" fill="#e5e5e5" />
					<p className="ui-widgets-tab-flow__empty-tab-container__title">
						{ isEmpty( tabs ) && emptyTabsMessage
							? emptyTabsMessage
							: 'Please select a tab to continue.' }
					</p>
				</div>
			) }
		</div>
	);
 };

// set display name
widgetsTabFlow.displayName = 'widgetsTabFlow';

// set default props
widgetsTabFlow.defaultProps = {
	activeTabIndex: 0,
	tabs: [],
	theme: 'default',
	onTabChange: noop,
	onTabActionClick: noop,
	emptyTabsMessage: null,
 };

// prop types
widgetsTabFlow.propTypes = { };
